'use es6';

import keyMirror from 'react-utils/keyMirror';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import { mapObject, omit, pick, unique, findKey, isEqual } from 'underscore';
import { byName as TemplateTypes } from 'ContentUtils/constants/TemplateTypes';
import { byId as TemplateTypesById } from 'ContentUtils/constants/TemplateTypes';
import { email } from 'ContentUtils/constants/Categories';
import { HUBSPOT_FOLDER_ID, MARKETPLACE_FOLDER_NAME, MARKETPLACE_FOLDER_PATH, ROOT_FOLDER_ID, PRETTIFIED_FOLDER_NAMES } from 'DesignData/constants/Folders';
import { DESIGN_ROOT, DESIGN_DEFAULT_LAYOUT } from 'DesignData/enums/FolderSubcategories';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { EMPTY_ARRAY } from 'DesignData/lib/array';
import { tryParseInt } from 'DesignData/lib/text';
import { isThemeRootFolder as __isThemeRootFolder } from 'DesignData/lib/themes/themes';
import { isServerlessFunctionFolder as __isServerlessFunctionFolder } from 'DesignData/lib/serverless/serverless';
import { isProjectsFolder as __isProjectsFolder } from 'DesignData/lib/projects/projects';
import { getAssetParentThemeFolderId } from 'DesignData/lib/themes/themes';
import { getAssetParentProjectFolderId } from 'DesignData/lib/projects/projects';
import { getActiveAssetId } from 'DesignData/selectors/app';
import { getIsServerlessDeploymentUngated, getIsUngatedForCustomThemeChildCreation } from 'DesignData/selectors/gates';
import { FINDER_BULK_PORTAL_COPY, FINDER_BULK_DELETE, FINDER_BULK_MOVE, FINDER_FILE_CREATE, FINDER_FILE_DELETE, FINDER_FILE_DUPLICATE, FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE, FINDER_FILE_RENAME, FINDER_FILE_SHOW_DEPENDENTS, FINDER_FILE_SHOW_REVISIONS, FINDER_FILE_SHOW_INFORMATION, FINDER_FILE_CREATE_PAGE, FINDER_FILE_CREATE_EMAIL, FINDER_FOLDER_COPY_PATH, FINDER_FOLDER_CREATE, FINDER_FOLDER_DELETE, FINDER_FOLDER_DUPLICATE, FINDER_FOLDER_MOVE_FILE, FINDER_FOLDER_MOVE_FOLDER, FINDER_FOLDER_OPEN_EDITOR, FINDER_FOLDER_RENAME, FINDER_FOLDER_TOGGLE_LOCK, FINDER_ASSETS_PORTAL_COPY, FINDER_FILE_COPY_PATH, FINDER_FILE_COPY_PUBLIC_URL, FINDER_FILE_COPY_MODULE_SNIPPET, FINDER_FILE_VIEW_LAYOUT_SOURCE, FINDER_THEMES_CREATE, FINDER_THEMES_CREATE_CHILD, FINDER_THEMES_CHILD_ADD_FILE, FINDER_THEMES_DUPLICATE, FINDER_FUNCTIONS_CREATE, FINDER_FUNCTIONS_DEPLOY } from 'DesignData/actions/ActionTypes';
import { SOURCE_CODE_WRITE, LANDING_PAGES_WRITE, PAGES_WRITE, EMAIL_WRITE } from 'DesignData/constants/Scopes';
import { FOLDER, TEMPLATE, LAYOUT, GLOBAL_GROUP, GLOBAL_WIDGET, CUSTOM_WIDGET, JS, CSS as FOLDER_ASSET_TYPE_CSS, RAW_ASSET } from 'DesignData/constants/FolderAssetTypes';
import { THEME_CONFIGURATION_FILES } from 'DesignData/constants/Themes';
import { FOCUS_FINDER_SEARCH } from 'DesignData/enums/FlashKeys';
import { PENDING } from 'DesignData/enums/RequestStatus';
import { canMoveFile, canMoveFolder, generateCopyName } from 'DesignData/helpers/finderHelpers';
import Folder from 'DesignData/models/finder/Folder';
import { hasEmailAccess as getHasEmailAccess, isScoped as getIsScoped, getHasServerlessFunctions, getUserHasReadAccessOnly, getHasDndTemplateAccess } from 'DesignData/selectors/auth';
import { getCustomWidgetsData } from 'DesignData/selectors/customWidgets';
import { getMarketplaceListings } from 'DesignData/selectors/ecosystem';
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);
const FILE_PERMISSIONS = Object.freeze(keyMirror({
  [FINDER_FILE_CREATE]: null,
  [FINDER_FILE_COPY_PATH]: null,
  [FINDER_FILE_DELETE]: null,
  [FINDER_FILE_DUPLICATE]: null,
  [FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE]: null,
  [FINDER_FILE_RENAME]: null,
  [FINDER_FILE_SHOW_DEPENDENTS]: null,
  [FINDER_FILE_SHOW_REVISIONS]: null,
  [FINDER_FILE_VIEW_LAYOUT_SOURCE]: null,
  [FINDER_FOLDER_MOVE_FILE]: null,
  [FINDER_FILE_SHOW_INFORMATION]: null,
  [FINDER_ASSETS_PORTAL_COPY]: null,
  [FINDER_THEMES_CHILD_ADD_FILE]: null
}));
const FOLDER_PERMISSIONS = Object.freeze(keyMirror({
  [FINDER_FOLDER_COPY_PATH]: null,
  [FINDER_FILE_CREATE]: null,
  [FINDER_FOLDER_CREATE]: null,
  [FINDER_FOLDER_DELETE]: null,
  [FINDER_FOLDER_DUPLICATE]: null,
  [FINDER_FOLDER_MOVE_FOLDER]: null,
  [FINDER_FOLDER_OPEN_EDITOR]: null,
  [FINDER_FOLDER_RENAME]: null,
  [FINDER_FOLDER_TOGGLE_LOCK]: null,
  [FINDER_ASSETS_PORTAL_COPY]: null,
  [FINDER_THEMES_CHILD_ADD_FILE]: null
}));
const BULK_PERMISSIONS = Object.freeze(keyMirror({
  [FINDER_BULK_PORTAL_COPY]: null,
  [FINDER_BULK_DELETE]: null,
  [FINDER_BULK_MOVE]: null
}));
const DEFAULT_ACTIONS = SeamlessImmutable({
  primaryActions: [],
  secondaryActions: [],
  deleteAction: []
});
const DEFAULT_SECONDARY_ACTIONS = SeamlessImmutable({});
const BULK_ACTIONS = SeamlessImmutable({
  primaryActions: [FINDER_BULK_MOVE],
  secondaryActions: [FINDER_BULK_PORTAL_COPY],
  deleteAction: [FINDER_BULK_DELETE]
});
const FOLDER_ACTIONS = SeamlessImmutable({
  primaryActions: [FINDER_FOLDER_COPY_PATH, FINDER_FOLDER_DUPLICATE, FINDER_FOLDER_RENAME],
  secondaryActions: [],
  deleteAction: [FINDER_FOLDER_DELETE]
});
const READONLY_FOLDER_ACTIONS = SeamlessImmutable({
  primaryActions: [FINDER_FOLDER_COPY_PATH, FINDER_FOLDER_DUPLICATE],
  secondaryActions: [],
  deleteAction: null
});
const THEME_FOLDER_ACTIONS = SeamlessImmutable({
  primaryActions: [FINDER_FOLDER_COPY_PATH, FINDER_THEMES_DUPLICATE, FINDER_THEMES_CREATE_CHILD, FINDER_ASSETS_PORTAL_COPY, FINDER_FOLDER_RENAME],
  secondaryActions: [FINDER_FOLDER_OPEN_EDITOR, FINDER_FOLDER_TOGGLE_LOCK],
  deleteAction: [FINDER_FOLDER_DELETE]
});
const HUBSPOT_THEME_FOLDER_ACTIONS = SeamlessImmutable({
  primaryActions: [FINDER_FOLDER_COPY_PATH, FINDER_THEMES_DUPLICATE],
  secondaryActions: [],
  deleteAction: null
});
const MARKETPLACE_THEME_FOLDER_ACTIONS = SeamlessImmutable({
  primaryActions: [FINDER_FOLDER_COPY_PATH],
  secondaryActions: [],
  deleteAction: null
});
const PROJECT_FOLDER_ACTIONS = SeamlessImmutable({
  primaryActions: [],
  secondaryActions: [],
  deleteAction: null
});
const SERVERLESS_FOLDER_ACTIONS = SeamlessImmutable({
  primaryActions: [FINDER_FOLDER_COPY_PATH, FINDER_FOLDER_DUPLICATE, FINDER_FOLDER_RENAME],
  secondaryActions: [FINDER_FOLDER_OPEN_EDITOR, FINDER_FOLDER_TOGGLE_LOCK],
  deleteAction: [FINDER_FOLDER_DELETE]
});
const FILE_PRIMARY_ACTIONS = SeamlessImmutable([FINDER_FILE_DUPLICATE, FINDER_FOLDER_MOVE_FILE, FINDER_FILE_RENAME]);
const FILE_SECONDARY_ACTIONS = SeamlessImmutable([]);
const FILE_DELETE_ACTIONS = SeamlessImmutable([FINDER_FILE_DELETE]);
const DEFAULT_FILE_ACTIONS = SeamlessImmutable({
  primaryActions: FILE_PRIMARY_ACTIONS,
  secondaryActions: FILE_SECONDARY_ACTIONS,
  deleteAction: FILE_DELETE_ACTIONS
});
const SECONDARY_ACTIONS_ORDER = SeamlessImmutable([FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE, FINDER_FILE_COPY_PATH, FINDER_FILE_COPY_PUBLIC_URL, FINDER_FILE_COPY_MODULE_SNIPPET, FINDER_ASSETS_PORTAL_COPY, FINDER_THEMES_CHILD_ADD_FILE, FINDER_FILE_CREATE_EMAIL, FINDER_FILE_CREATE_PAGE, FINDER_FILE_SHOW_DEPENDENTS, FINDER_FILE_SHOW_INFORMATION, FINDER_FILE_SHOW_REVISIONS, FINDER_FILE_VIEW_LAYOUT_SOURCE]);
const DEFAULT_ASSET_PERMISSIONS = EMPTY_OBJECT;
const DEFAULT_FILE_PERMISSIONS = SeamlessImmutable(mapObject(FILE_PERMISSIONS, () => false));
const DEFAULT_FOLDER_PERMISSIONS = SeamlessImmutable(mapObject(FOLDER_PERMISSIONS, () => false));
const DEFAULT_BULK_PERMISSIONS = SeamlessImmutable(mapObject(BULK_PERMISSIONS, () => false));

const getAssetIdFromProps = (state, props) => props.fileId || props.folderId || props.assetId;

const getFileIdFromProps = (state, props) => props.fileId;

const getFolderIdFromProps = (state, props) => props.folderId || props.assetId;

export const getFilesById = state => state.finder.files.byId;
export const getFile = (state, props = {}) => {
  let fileId;

  if (props.fileId) {
    fileId = props.fileId;
  } else if (props.file && props.file.id) {
    fileId = props.file.id;
  }

  if (fileId && state.finder.files.byId[fileId]) {
    return state.finder.files.byId[fileId];
  }

  return props.file;
};
/*
 * Themes
 */

export const getActiveThemeFolderId = state => state.finder.themes.activeThemeFolderId;
export const getIsThemeCreationInProgress = state => state.finder.themes.themeCreationInProgress;
export const getChildThemes = state => state.finder.themes.childThemes;
export const getChildThemeCreationStatus = state => state.finder.themes.childThemeCreationStatus;
export const getChildThemeCreationError = state => state.finder.themes.childThemeCreationError;
export const getIsFetchingChildThemes = state => state.finder.themes.isFetchingChildThemes;
export const getParentThemeId = state => state.finder.themes.parentThemeId;
export const getParentThemeFileStatus = state => state.finder.themes.parentThemeFileStatus;
export const getParentThemeFileError = state => state.finder.themes.parentThemeFileError;
/*
  Serverless Functions
*/

export const getIsFunctionCreationInProgress = state => state.finder.functions.functionCreationInProgress;
export const getIsFunctionDeploymentInProgress = state => state.finder.functions.functionDeploymentInProgress;
export const getFunctionDeploymentLogs = state => state.finder.functions.functionDeploymentLogs;
export const getCurrentlyUploadingFileList = state => Object.values(state.finder.files.currentlyUploading);
/*
  Projects
*/

export const getActiveProjectFolderId = state => state.finder.projects.activeProjectFolderId;
export const getIsProjectCreationInProgress = state => state.finder.projects.projectCreationInProgress;
export const getIsProjectDeploymentInProgress = state => state.finder.projects.projectDeploymentInProgress;
/*
 * Folders
 */

export const getFoldersById = state => state.finder && state.finder.folders.byId;
export const getFolderContentsById = (state, props) => state.finder.folders.contentsById[props.folderId];
export const getFolder = (state, props) => {
  const folderId = getFolderIdFromProps(state, props);
  return state.finder.folders.byId[folderId];
};

const getIsFolderReadOnly = (state, props) => {
  const folder = getFolder(state, props);
  if (!folder) return false;
  return folder.readOnly || folder.isDefault || false;
};

export const getIsHubspotFolder = (state, props) => {
  const folderId = getFolderIdFromProps(state, props);
  if (!folderId) return false;

  if (typeof folderId === 'string' && folderId.indexOf(HUBSPOT_FOLDER_ID) === 0) {
    return true;
  }

  const folder = getFolder(state, props);
  return !!folder && folder.isDefault === true;
};
export const getDefaultsFetchStatus = state => state.finder.folders.defaultsFetchStatus;

const getOpenFoldersById = state => state.finder.folders.openById;

export const getRootFolderId = state => state.finder.folders.rootFolderId;
/**
 * Root folderId of active view.
 * These are the regular root folder or special sub-folders
 * that are given their own "view" in the finder UI. E.g. theme folders
 */

export const getActiveRootFolderId = state => {
  const rootFolderId = getRootFolderId(state); // Don't return active sub-folder until main rootFolderId is determined.
  // This is to avoid prop errors in components.

  if (!rootFolderId) return rootFolderId;
  return getActiveThemeFolderId(state) || getActiveProjectFolderId(state) || rootFolderId;
};
export const getRootFolderContentsRequestStatus = state => state.finder.folders.rootFolderContentsRequestStatus;
export const getRootFolderContentsFetchError = state => state.finder.folders.fetchRootFolderContentsError;
export const getIsFetchingRootFolderContents = createSelector([getRootFolderContentsRequestStatus], requestStatus => requestStatus === PENDING);
export const getIsRootFolder = createSelector([getFolderIdFromProps, getRootFolderId], (folderId, rootFolderId) => folderId === rootFolderId || folderId === ROOT_FOLDER_ID);
export const getAvailableForNewContentThemeIds = state => state.finder.themes.themes.filter(t => t.isAvailableForNewContent).map(t => t.folderId);

const getCurrentRenameAssetId = state => state.finder.options.currentRenameAsset.assetId;

const getCurrentRenameAssetIsDuplicate = state => state.finder.options.currentRenameAsset.isDuplicate;

export const getShowRenameFileConfirmOption = state => state.finder.options.showRenameFileConfirm;

const getShowRenameFolderConfirmOption = state => state.finder.options.showRenameFolderConfirm;

export const getShowRecentlyEdited = state => state.finder.assets.showRecentlyEdited;
export const getShowRecentlyDeleted = state => state.finder.assets.showRecentlyDeleted;
export const getIsSearchFocusRequested = state => state.flash[FOCUS_FINDER_SEARCH];
export const getActionModal = state => state.finder.actionModal;
export const getFolderMetadataById = state => state.finder && state.finder.folders.folderMetadataById;

const getFolderMetadata = (state, props) => {
  const folderId = props.assetId || props.folderId;
  const folderMetadataById = getFolderMetadataById(state);
  return folderMetadataById[folderId] || EMPTY_OBJECT;
};

export const makeGetIsThemeAvailableForNewContent = () => createSelector([getActiveThemeFolderId, getAvailableForNewContentThemeIds], (activeThemeFolderId, themeIds) => !activeThemeFolderId || themeIds.includes(activeThemeFolderId));
export const makeGetActiveThemeFolder = () => {
  return createSelector([getActiveThemeFolderId, getFoldersById], (activeThemeFolderId, foldersById) => {
    return foldersById[activeThemeFolderId];
  });
};
/**
 * Finder selections
 */

export const getRevealedAssetId = state => state.finder.selections.revealedAssetId;
export const getLastSelectedAssetId = state => state.finder.selections.selectedAssets.lastSelected;
export const getLastDeselectedAssetId = state => state.finder.selections.selectedAssets.lastDeselected;

const getSelectedAssetsOrder = state => state.finder.selections.selectedAssets.order;

const getSelectedAssetsById = state => state.finder.selections.selectedAssets.byId;

export const getSelectedAssetsCount = createSelector([getSelectedAssetsOrder], order => order.length);
export const getHasSelections = createSelector([getSelectedAssetsCount], selectedAssetsCount => !!selectedAssetsCount);
export const getHasMultipleSelections = createSelector([getSelectedAssetsCount], selectedAssetsCount => selectedAssetsCount > 1);
const DEFAULT_MODELS_BY_ID = EMPTY_OBJECT;

const makeGetSelectedAssetsModelsById = () => {
  return createSelector([getSelectedAssetsCount, getSelectedAssetsById, getFoldersById, getFilesById], (count, byId, foldersById, filesById) => {
    if (!count) {
      return DEFAULT_MODELS_BY_ID;
    }

    const modelsById = Object.keys(byId).reduce((hash, assetId) => {
      const {
        assetType,
        parentFolderId
      } = byId[assetId];
      const asset = assetType === FOLDER ? foldersById[assetId] : filesById[assetId];

      if (!asset) {
        return hash;
      }

      hash[assetId] = asset.merge({
        parentFolderId
      });
      return hash;
    }, {});
    return SeamlessImmutable(modelsById);
  });
};

const DEFAULT_ASSETS = EMPTY_ARRAY;

const makeGetSelectedAssets = () => {
  const getSelectedAssetModelsById = makeGetSelectedAssetsModelsById();
  let assets = DEFAULT_ASSETS;
  return createSelector([getSelectedAssetsCount, getSelectedAssetsOrder, getSelectedAssetModelsById], (count, order, modelsById) => {
    if (!count) {
      return DEFAULT_ASSETS;
    }

    const __assets = order.map(assetId => modelsById[assetId]); // Filter any child assets of selected folders from the list


    assets = __assets.filter(asset => asset && !order.includes(asset.parentFolderId));
    return assets;
  });
};

export const getSelectedAssets = makeGetSelectedAssets();

const makeGetIsAssetSelected = () => createSelector([getAssetIdFromProps, getSelectedAssetsById], (assetId, selectedAssetsById) => {
  return Boolean(assetId && selectedAssetsById[assetId]);
});

export const makeGetIsAssetMultiSelected = () => {
  const getIsAssetSelected = makeGetIsAssetSelected();
  return createSelector([getIsAssetSelected, getHasMultipleSelections], (isSelected, hasMultipleSelections) => isSelected && hasMultipleSelections);
};
export const getActiveFileId = createSelector([getSelectedAssetsOrder, getSelectedAssetsById], (order, byId) => {
  const selectedAsset = byId[order[0]];
  const isFile = selectedAsset && selectedAsset.assetType !== FOLDER;
  return order.length === 1 && isFile ? selectedAsset.assetId : null;
});
export const getActiveFolderId = createSelector([getSelectedAssetsOrder, getSelectedAssetsById], (order, byId) => {
  const selectedAsset = byId[order[0]];
  const isFolder = selectedAsset && selectedAsset.assetType === FOLDER;
  return order.length === 1 && isFolder ? selectedAsset.assetId : null;
});
/**
 * Base user permissions.
 */

export const getUserPermissions = createSelector([getIsScoped, getUserHasReadAccessOnly, getHasEmailAccess, getActiveThemeFolderId], (isScoped, userHasReadAccessOnly, hasEmailAccess, activeThemeFolderId) => {
  const hasSourceCodeWriteAccess = isScoped(SOURCE_CODE_WRITE);
  const canCreatePage = isScoped(LANDING_PAGES_WRITE) || isScoped(PAGES_WRITE);
  const canCreateEmail = hasEmailAccess && isScoped(EMAIL_WRITE);
  const canCopyToPortal = !activeThemeFolderId && hasSourceCodeWriteAccess;
  return {
    [FINDER_FILE_CREATE]: hasSourceCodeWriteAccess,
    [FINDER_FILE_DELETE]: hasSourceCodeWriteAccess,
    [FINDER_FILE_DUPLICATE]: hasSourceCodeWriteAccess,
    [FINDER_FILE_RENAME]: hasSourceCodeWriteAccess,
    [FINDER_FILE_SHOW_DEPENDENTS]: hasSourceCodeWriteAccess || userHasReadAccessOnly,
    [FINDER_FILE_SHOW_REVISIONS]: hasSourceCodeWriteAccess || userHasReadAccessOnly,
    [FINDER_FILE_SHOW_INFORMATION]: hasSourceCodeWriteAccess,
    [FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE]: hasSourceCodeWriteAccess,
    [FINDER_FOLDER_COPY_PATH]: hasSourceCodeWriteAccess || userHasReadAccessOnly,
    [FINDER_FOLDER_CREATE]: hasSourceCodeWriteAccess,
    [FINDER_FOLDER_DELETE]: hasSourceCodeWriteAccess,
    [FINDER_FOLDER_DUPLICATE]: hasSourceCodeWriteAccess,
    [FINDER_FOLDER_MOVE_FILE]: hasSourceCodeWriteAccess,
    [FINDER_FOLDER_MOVE_FOLDER]: hasSourceCodeWriteAccess,
    [FINDER_FOLDER_OPEN_EDITOR]: hasSourceCodeWriteAccess,
    [FINDER_FOLDER_RENAME]: hasSourceCodeWriteAccess,
    [FINDER_FOLDER_TOGGLE_LOCK]: hasSourceCodeWriteAccess,
    [FINDER_ASSETS_PORTAL_COPY]: canCopyToPortal,
    [FINDER_FILE_CREATE_PAGE]: canCreatePage,
    [FINDER_FILE_CREATE_EMAIL]: canCreateEmail,
    [FINDER_FILE_COPY_PATH]: hasSourceCodeWriteAccess || userHasReadAccessOnly,
    [FINDER_FILE_COPY_PUBLIC_URL]: hasSourceCodeWriteAccess || userHasReadAccessOnly,
    [FINDER_FILE_COPY_MODULE_SNIPPET]: hasSourceCodeWriteAccess,
    [FINDER_FILE_VIEW_LAYOUT_SOURCE]: hasSourceCodeWriteAccess || userHasReadAccessOnly,
    [FINDER_THEMES_CHILD_ADD_FILE]: hasSourceCodeWriteAccess
  };
});
/**
 * Get root folder
 */

export const getRootFolder = createSelector([getRootFolderId, getFoldersById], (rootFolderId, foldersById) => {
  return rootFolderId ? foldersById[rootFolderId] : null;
});
/**
 * Get active file model
 */

export const getActiveFile = createSelector([getActiveFileId, getFilesById], (activeFileId, filesById) => filesById[activeFileId]);
/**
 * Get active folder model
 */

export const getActiveFolder = createSelector([getActiveFolderId, getFoldersById], (activeFolderId, foldersById) => foldersById[activeFolderId]);
/**
 * Get is folder open
 */

export const getIsFolderOpen = (state, props) => state.finder.folders.openById[props.folderId];
/**
 * Get open folder IDs
 */

export const getOpenFolderIds = createSelector([getOpenFoldersById, getRootFolderId], (openFoldersById, rootFolderId) => {
  return Object.keys(omit(openFoldersById, rootFolderId));
});
/**
 * Get open folder models
 */

export const getOpenFolders = createSelector([getFoldersById, getOpenFolderIds], (foldersById, openFolderIds) => {
  return pick(foldersById, openFolderIds);
});
/**
 * Get open folders count (excluding root folder)
 */

export const getOpenFoldersCount = createSelector([getFoldersById, getOpenFolderIds], (foldersById, openFolderIds) => {
  return openFolderIds.reduce((count, folderId) => {
    const folder = foldersById[folderId];
    return folder && folder.subcategory !== DESIGN_ROOT ? ++count : count;
  }, 0);
});
export const getAssetIds = (state, props) => props.assetIds;
export const makeGetAssets = () => {
  return createSelector([getFoldersById, getFilesById, getAssetIds], (foldersById, filesById, assetIds) => {
    return assetIds.map(assetId => {
      const asset = filesById[assetId] || foldersById[assetId];
      return {
        assetId,
        assetType: asset.type || FOLDER,
        parentFolderId: asset.parentFolderId || asset.folderId
      };
    });
  });
};
/**
 * Required props:
 *  - assetId || fileId || folderId
 *
 * @returns {Number|Null}
 */

export const makeGetAssetParentThemeFolderId = () => {
  return createSelector([getFilesById, getFoldersById, getAssetIdFromProps], getAssetParentThemeFolderId);
};
/**
 * Required props:
 *  - assetId || fileId || folderId
 *
 * @returns {Folder|Null}
 */

export const makeGetAssetParentThemeFolder = () => {
  return createSelector([getFilesById, getFoldersById, getAssetIdFromProps], (filesById, foldersById, assetId) => {
    const assetParentThemeFolderId = getAssetParentThemeFolderId(filesById, foldersById, assetId);
    return foldersById[assetParentThemeFolderId];
  });
};
/**
 *
 * @returns {Folder|Null}
 */

export const makeGetActiveAssetParentThemeFolder = () => {
  return createSelector([getFilesById, getFoldersById, getActiveAssetId], (filesById, foldersById, assetId) => {
    const assetParentThemeFolderId = getAssetParentThemeFolderId(filesById, foldersById, assetId);
    return foldersById[assetParentThemeFolderId];
  });
};
export const getFileUploadProgress = (state, props) => {
  const uploading = state.finder.files.currentlyUploading;
  const key = findKey(uploading, file => file.folderId === props.folderId);

  if (uploading && key) {
    return uploading[key].progress;
  }

  return null;
};
export const getFolderCurrentlyUploadingFilesList = (state, props) => {
  const uploadingFiles = Object.values(state.finder.files.currentlyUploading);
  const fileInfo = [];
  uploadingFiles.forEach(file => {
    if (file.folderId === props.folderId) {
      fileInfo.push({
        id: file.fileName,
        fullPath: file.fullPath,
        type: '',
        name: file.fileName
      });
    }
  });
  return fileInfo;
};
export const makeGetFolderContents = () => {
  return createDeepEqualSelector([getFolderContentsById, getCurrentlyUploadingFileList, getFolderIdFromProps], (contents, uploadingFiles, folderId) => {
    const uploadingFileNames = [];
    uploadingFiles.forEach(file => {
      if (file.folderId === folderId) {
        uploadingFileNames.push(file.fileName);
      }
    });

    if (!contents) {
      return SeamlessImmutable({
        subfolders: [],
        files: [],
        uploading: []
      });
    }

    return contents.merge({
      uploading: uploadingFileNames
    });
  });
};
export const getFolderSubfolderNames = (state, props) => {
  const getFolderContents = makeGetFolderContents();
  const contents = getFolderContents(state, props);
  const foldersById = getFoldersById(state, props);
  if (!contents || !foldersById) return EMPTY_ARRAY;
  const {
    subfolders
  } = contents;
  const list = [];
  subfolders.forEach(_id => {
    const folder = foldersById[_id];
    if (!folder || !folder.label) return;
    list.push(folder.label);
  });
  return SeamlessImmutable(list);
};

const makeGetFolderContentsList = () => {
  const getFolderContents = makeGetFolderContents();
  return createSelector([getFolderContents, getFoldersById, getFilesById], (contents, foldersById, filesById) => {
    if (!contents) return EMPTY_ARRAY;
    const {
      subfolders,
      files
    } = contents;
    const list = [];
    subfolders.forEach(_id => {
      const folder = foldersById[_id];
      if (!folder) return;
      list.push(folder);
    });
    files.forEach(_id => {
      const file = filesById[_id];
      if (!file) return;
      list.push(file);
    });
    return SeamlessImmutable(list);
  });
};

export const makeGetFolderContentsOrder = () => {
  const getFolderContentsList = makeGetFolderContentsList();
  return createDeepEqualSelector([getFolderContentsList, getFolderCurrentlyUploadingFilesList], (contentsList, uploadList) => {
    if (!contentsList) return EMPTY_ARRAY;
    const order = [].concat(contentsList).concat(uploadList).sort((assetA, assetB) => {
      // @hubspot folder first
      if (assetA.id === HUBSPOT_FOLDER_ID) return -1;
      if (assetB.id === HUBSPOT_FOLDER_ID) return 1; // @marketplace folder second

      if (assetA.fullPath === MARKETPLACE_FOLDER_PATH) return -1;
      if (assetB.fullPath === MARKETPLACE_FOLDER_PATH) return 1; // Folders first

      if (assetA.type === FOLDER && assetB.type !== FOLDER) {
        return -1;
      }

      if (assetB.type === FOLDER && assetA.type !== FOLDER) {
        return 1;
      } // Sort ASC


      const nameA = assetA.name.toLowerCase();
      const nameB = assetB.name.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    }).map(({
      id
    }) => tryParseInt(id));
    return SeamlessImmutable(order);
  });
};
export const makeGetFolderContentsCount = () => {
  const __getFolderContentsOrder = makeGetFolderContentsOrder();

  return createSelector([__getFolderContentsOrder], order => order.length);
};
export const getNestedFolderContents = (state, props, __parentContents = {
  files: [],
  subfolders: []
}) => {
  const getFolderContents = makeGetFolderContents();
  const folderContents = getFolderContents(state, props);

  if (!folderContents) {
    return __parentContents;
  }

  const {
    files,
    subfolders
  } = folderContents;
  const {
    files: parentFiles,
    subfolders: parentSubfolders
  } = __parentContents;
  let parentContents = {
    files: parentFiles.concat(files),
    subfolders: parentSubfolders.concat(subfolders)
  };
  subfolders.forEach(__subfolderId => {
    parentContents = getNestedFolderContents(state, {
      folderId: __subfolderId
    }, parentContents);
  });
  return {
    files: unique(parentContents.files),
    subfolders: unique(parentContents.subfolders)
  };
};
/**
 * Memoize isFetchingFolderContentsById by folder ID
 */

export const getIsFetchingFolderContents = (state, props) => {
  return state.finder.folders.isFetchingContentsById[props.folderId];
};
export const makeGetIsFetchingFolderContents = () => createSelector([getFolder, getIsFetchingRootFolderContents, getIsFetchingFolderContents], (folder, isFetchingRootFolderContents, isFetchingContents) => {
  return folder && folder.subcategory === DESIGN_ROOT ? isFetchingRootFolderContents : isFetchingContents;
});
/**
 * Memoize fetchContentsErrorById by folder ID
 */

const getFetchFolderContentsError = (state, props) => state.finder.folders.fetchContentsErrorById[props.folderId];

export const makeGetFetchFolderContentsError = () => createSelector([getFetchFolderContentsError], fetchContentsError => fetchContentsError);
/**
 * Memoize folder open state
 */

const getFolderOpenState = (state, props) => state.finder.folders.openById[props.folderId];

export const makeGetFolderOpenState = () => createSelector([getFolder, getFolderOpenState], (folder, isOpen) => {
  return folder && folder.subcategory === DESIGN_ROOT || isOpen || false;
});
/**
 * Memoize file types
 */

const makeGetIsGlobalGroup = () => createSelector([getFile], file => file && file.type === GLOBAL_GROUP);

const makeGetIsGlobalWidget = () => createSelector([getFile], file => file && file.type === GLOBAL_WIDGET);

const makeGetIsCustomWidget = () => createSelector([getFile], file => file && file.type === CUSTOM_WIDGET);

const makeGetIsLayout = () => createSelector([getFile], file => file && file.type === LAYOUT);

const makeGetIsTemplate = () => createSelector([getFile], file => file && file.type === TEMPLATE);

const makeGetIsCodedFile = () => createSelector([getFile], file => {
  return file && (file.type === TEMPLATE || file.type === FOLDER_ASSET_TYPE_CSS || file.type === JS);
});

const makeGetIsRawAsset = () => createSelector([getFile], file => file && file.type === RAW_ASSET);

const makeGetTemplateType = () => createSelector([getFile], file => file && file.templateType);

const makeGetIsTemplatePartial = () => createSelector([getFile], file => file && file.availableForNewContent === false);
/**
 * Memoize folder paths
 */


const getFolderAncestors = (state, props, ancestors = []) => {
  const {
    parentFolderId,
    subcategory
  } = getFolder(state, props) || {};

  if (!parentFolderId || subcategory === DESIGN_ROOT) {
    return ancestors;
  }

  const parentFolderProps = {
    folderId: parentFolderId
  };
  const parentFolder = getFolder(state, parentFolderProps);

  if (!parentFolder) {
    return ancestors;
  }

  return getFolderAncestors(state, parentFolderProps, [parentFolder, ...ancestors]);
};

const getFolderAncestorsWithoutRoot = (state, props) => {
  return getFolderAncestors(state, props).filter(_folder => _folder && _folder.subcategory !== DESIGN_ROOT);
};

export const makeGetFolderPath = () => {
  const initialFolderPath = EMPTY_ARRAY;
  let folderPath = initialFolderPath;
  return createSelector([getFolder, getFolderAncestorsWithoutRoot], (folder, ancestors) => {
    if (!folder || folder.subcategory === DESIGN_ROOT) {
      return initialFolderPath;
    }

    const ancestorFolderPaths = ancestors.map(_folder => _folder.name);
    folderPath = SeamlessImmutable([...ancestorFolderPaths, folder.name]);
    return folderPath;
  });
};
export const makeGetFolderAncestorIds = () => {
  const initialFolderIds = EMPTY_ARRAY;
  let folderIds = initialFolderIds;
  return createSelector([getFolder, getFolderAncestors], (folder, ancestors) => {
    if (!folder) {
      return initialFolderIds;
    }

    const ancestorFolderIds = ancestors.map(_folder => _folder.id);
    folderIds = SeamlessImmutable([...ancestorFolderIds, folder.id]);
    return folderIds;
  });
};
/**
 *  Locked Folders Selectors
 */

export const getIsLocked = (state, props) => {
  const folderMetadata = getFolderMetadata(state, props);
  return folderMetadata && folderMetadata.locked || false;
};
export const getFolderAncestorsFactory = createSelector([getFoldersById], foldersById => {
  return folderId => {
    const ancestors = [];
    let currFolder = foldersById && foldersById[folderId];

    while (currFolder && currFolder.parentFolderId) {
      const parentFolderId = currFolder.parentFolderId;
      currFolder = foldersById[parentFolderId];
      ancestors.push(parentFolderId);
    }

    return ancestors;
  };
});
export const getIsAncestorLockedFactory = createSelector([getFolderAncestorsFactory, getFolderMetadataById], (getFolderAncestorsFromFolderId, folderMetadataById) => {
  return folderId => {
    const ancestors = getFolderAncestorsFromFolderId(folderId);

    for (const ancestorId of ancestors) {
      const folderMetadata = folderMetadataById[ancestorId];

      if (folderMetadata && folderMetadata.locked) {
        return true;
      }
    }

    return false;
  };
});
/**
 *  USE THIS SELECTOR SPARINGLY
 *  This selector performs a recursive check to see if ancestors
 *  are locked. If locked status is needed within a tree like structure,
 *  pass locked status via props using `getIsLocked`.
 *
 *    Example:
 *    https://git.hubteam.com/HubSpot/DesignManagerUI/pull/5046
 *
 *  THIS SELECTOR RETURNS A MEMOIZED FUNCTION
 *  Not a value. This is to avoid abusing the prop parameter and
 *  breaking memoization.
 *
 *    Background:
 *    https://github.com/reduxjs/reselect#q-how-do-i-create-a-selector-that-takes-an-argument
 *    https://git.hubteam.com/HubSpot/DesignManagerUI/pull/4952
 *
 */

export const getIsInLockedFolderFactory = createSelector([getFolderMetadataById, getIsAncestorLockedFactory], (folderMetadataById, getIsAncestorLocked) => {
  return folderId => {
    const folderMetadata = folderMetadataById && folderMetadataById[folderId];
    const isLocked = folderMetadata && folderMetadata.locked;
    const isAncestorLocked = getIsAncestorLocked(folderId);
    return isLocked || isAncestorLocked;
  };
});
export const getFileIsInLockedFolder = createSelector([getFile, getIsInLockedFolderFactory], (file, getIsInLockedFolder) => {
  if (!file) {
    return false;
  }

  return getIsInLockedFolder(file.folderId);
});
export const getIsServerlessFunctionFolder = (state, props) => {
  const folder = getFolder(state, props);
  return __isServerlessFunctionFolder(folder);
};
export const getIsAncestorServerlessFunctionFactory = createSelector([getFolderAncestorsFactory, getFoldersById], (getFolderAncestorsFromFolderId, getFolderById) => {
  return folderId => {
    const ancestors = getFolderAncestorsFromFolderId(folderId);

    for (const ancestorId of ancestors) {
      const folder = getFolderById[ancestorId];

      if (__isServerlessFunctionFolder(folder)) {
        return true;
      }
    }

    return false;
  };
});
export const getParentServerlessFunctionFolder = createSelector([getFolder, getFolderAncestors], (folder, folderAncestors) => {
  if (__isServerlessFunctionFolder(folder)) {
    return folder;
  }

  const serverlessFunctionFolder = folderAncestors.find(ancestor => __isServerlessFunctionFolder(ancestor));
  return serverlessFunctionFolder;
});
export const getParentServerlessFunctionConfigId = (state, props) => {
  const parentServerlessFunctionFolder = getParentServerlessFunctionFolder(state, props);
  if (!parentServerlessFunctionFolder) return null;
  const getFolderContents = makeGetFolderContents();
  const serverlessFolderContents = getFolderContents(state, {
    folderId: parentServerlessFunctionFolder.id
  });
  const filesById = getFilesById(state, props);
  return serverlessFolderContents && serverlessFolderContents.files.filter(fileId => {
    const fileInfo = filesById[fileId];
    return fileInfo.name === 'serverless.json';
  })[0];
};
export const getIsStagedFile = createSelector([getFile], file => {
  return file && file.staged;
});
export const getIsProjectsFolder = (state, props) => {
  const folder = getFolder(state, props);
  return __isProjectsFolder(folder);
};
export const getIsAncestorProjectsFolder = createSelector([getFolderIdFromProps, getFolderAncestorsFactory, getFoldersById], (folderId, getFolderAncestorsFromFolderId, getFolderById) => {
  const ancestors = getFolderAncestorsFromFolderId(folderId);

  for (const ancestorId of ancestors) {
    const folder = getFolderById[ancestorId];

    if (__isProjectsFolder(folder)) {
      return true;
    }
  }

  return false;
});
export const getIsInProjectsFolder = createSelector([getIsProjectsFolder, getIsAncestorProjectsFolder], (isProjectsFolder, ancestorIsProjectsFolder) => {
  return isProjectsFolder || ancestorIsProjectsFolder;
});
export const getIsProjectsFile = (state, props) => {
  const file = getFile(state, {
    fileId: props.fileId
  });

  if (!file) {
    return null;
  }

  return file && !!file.projectId;
};
/**
 * Required props:
 *  - assetId || fileId || folderId
 *
 * @returns {Number|Null}
 */

export const makeGetAssetParentProjectFolderId = () => {
  return createSelector([getFilesById, getFoldersById, getAssetIdFromProps], getAssetParentProjectFolderId);
};
/**
 * Required props:
 *  - assetId || fileId || folderId
 *
 * @returns {Folder|Null}
 */

export const makeGetAssetParentProjectFolder = () => {
  return createSelector([getFilesById, getFoldersById, getAssetIdFromProps], (filesById, foldersById, assetId) => {
    const assetParentProjectFolderId = getAssetParentProjectFolderId(filesById, foldersById, assetId);
    return foldersById[assetParentProjectFolderId];
  });
};
/**
 * Required Props:
 *  - assetId || folderId
 */

export const getIsThemeRootFolder = (state, props) => {
  const folder = getFolder(state, props);
  return __isThemeRootFolder(folder);
};
/**
 * Required Props:
 *  - assetId || folderId
 */

export const getHasMarketplaceListing = createSelector([getFolder, getIsThemeRootFolder, getMarketplaceListings], (folder, isThemeRootFolder, marketplaceListings) => {
  return folder && isThemeRootFolder && marketplaceListings.includes(folder.fullPath);
});
/**
 * Returns function `folderId => Boolean`
 * which returns true if `folderId` is a theme folder.
 */

export const getEvaluateIsThemeRootFolder = createSelector([getFoldersById], foldersById => {
  return folderId => __isThemeRootFolder(foldersById[folderId]);
});
/**
 * Required Props:
 *  - fileId || file
 *
 * Theme config files will be root children of a theme folder.
 */

const makeGetIsThemeConfigurationFile = () => {
  return createSelector([getFile, getFoldersById], (file, foldersById) => {
    if (file == null || !THEME_CONFIGURATION_FILES.has(file.name)) {
      return false;
    }

    const folder = foldersById[file.folderId];
    return __isThemeRootFolder(folder);
  });
};

export const getAncestorThemePathFactory = createSelector([getFolderAncestorsFactory, getFoldersById], (getFolderAncestorsFromFolderId, getFolderById) => {
  return folderId => {
    const ancestors = getFolderAncestorsFromFolderId(folderId);

    for (const ancestorId of ancestors) {
      const folder = getFolderById[ancestorId];

      if (folder && __isThemeRootFolder(folder)) {
        return folder.fullPath;
      }
    }

    return null;
  };
});
export const getThemePathFromAncestors = createSelector([getFoldersById, getAncestorThemePathFactory], (getFolderById, getAncestorThemePath) => {
  return folderId => {
    const folder = getFolderById[folderId];

    if (folder && __isThemeRootFolder(folder)) {
      return folder.fullPath;
    }

    return getAncestorThemePath(folderId);
  };
}); //Passing folder as an argument to use in fetchChildThemes action

export const getThemePathFromFolder = createSelector([getAncestorThemePathFactory], getAncestorThemePath => folder => {
  if (folder && __isThemeRootFolder(folder)) {
    return folder.fullPath;
  }

  return getAncestorThemePath(folder.id);
}); //Passing file as an argument to use in fetchChildThemes action

export const getThemePathFromFile = createSelector([getThemePathFromAncestors], getThemePath => file => {
  if (!file) {
    return null;
  }

  return getThemePath(file.folderId);
});
export const getIsFileIdInThemeFolder = createSelector([getThemePathFromAncestors, getFilesById], (getThemePath, filesById) => fileId => {
  const {
    folderId
  } = filesById[fileId];

  if (!folderId) {
    return false;
  }

  return !!getThemePath(folderId);
});

const getIsOrIsInMarketplaceFolder = path => {
  if (path) {
    const hasLeadingSlash = path.startsWith('/');
    const startsWithMarketplace = path.startsWith(`${hasLeadingSlash ? '/' : ''}${PRETTIFIED_FOLDER_NAMES[MARKETPLACE_FOLDER_NAME]}`) || path.startsWith(MARKETPLACE_FOLDER_NAME);

    if (startsWithMarketplace) {
      const rootFolder = path.split('/')[hasLeadingSlash ? 1 : 0];
      return rootFolder === PRETTIFIED_FOLDER_NAMES[MARKETPLACE_FOLDER_NAME] || rootFolder === MARKETPLACE_FOLDER_NAME;
    }
  }

  return false;
};

export const getFolderIsOrIsInMarketplaceFolder = createSelector([getFolder], folder => folder && getIsOrIsInMarketplaceFolder(folder.fullPath));
export const getFileIsInMarketplaceFolder = createSelector([getFile], file => {
  return file && getIsOrIsInMarketplaceFolder(file.path);
});
/**
 * Memoize file actions state
 */

const makeGetFileActionsState = () => createSelector([getFileIdFromProps, getCurrentRenameAssetId], (fileId, currentRenameAssetId) => {
  return {
    [FINDER_FILE_RENAME]: currentRenameAssetId === fileId
  };
});

const getIsReadOnly = createSelector([getFile, getIsInLockedFolderFactory], (file, getIsInLockedFolder) => {
  if (!file) {
    return false;
  }

  const isLocked = getIsInLockedFolder(file.folderId);
  return file.isReadOnly || file.isDefault || isLocked;
});
/**
 * Memoize file permissions
 */

export const makeGetFilePermissions = () => {
  let permissions = DEFAULT_FILE_PERMISSIONS;

  const __getIsGlobalGroup = makeGetIsGlobalGroup();

  const __getIsGlobalWidget = makeGetIsGlobalWidget();

  const __getIsCustomWidget = makeGetIsCustomWidget();

  const __getIsLayout = makeGetIsLayout();

  const __getIsTemplate = makeGetIsTemplate();

  const __getIsCodedFile = makeGetIsCodedFile();

  const __getFileActionsState = makeGetFileActionsState();

  const __getTemplateType = makeGetTemplateType();

  const __getIsTemplatePartial = makeGetIsTemplatePartial();

  const __getIsRawAsset = makeGetIsRawAsset();

  const __getIsThemeAvailableForNewContent = makeGetIsThemeAvailableForNewContent();

  return createSelector([getUserPermissions, __getIsGlobalGroup, __getIsGlobalWidget, __getIsCustomWidget, __getIsLayout, __getIsTemplate, __getIsCodedFile, __getIsRawAsset, __getFileActionsState, __getTemplateType, __getIsTemplatePartial, __getIsThemeAvailableForNewContent, getIsReadOnly, getFileIsInLockedFolder, getFileIsInMarketplaceFolder, getHasDndTemplateAccess], (userPermissions, isGlobalGroup, isGlobalWidget, isCustomWidget, isLayout, isTemplate, isCodedFile, isRawAsset, actionsState, templateType, isTemplatePartial, isThemeAvailableForNewContent, isReadOnly, isFileLocked, isMarketplaceAsset, hasDnDTemplateAccess) => {
    const {
      [FINDER_FILE_DELETE]: canDelete,
      [FINDER_FILE_DUPLICATE]: canDuplicate,
      [FINDER_FOLDER_MOVE_FILE]: canMove,
      [FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE]: canCloneLayoutToTemplate,
      [FINDER_FILE_CREATE_PAGE]: canCreatePage,
      [FINDER_FILE_CREATE_EMAIL]: canCreateEmail,
      [FINDER_FILE_COPY_PUBLIC_URL]: canCopyPublicUrl,
      [FINDER_FILE_COPY_MODULE_SNIPPET]: canCopyModuleSnippet,
      [FINDER_FILE_RENAME]: canRename,
      [FINDER_FILE_VIEW_LAYOUT_SOURCE]: canViewLayoutSource,
      [FINDER_ASSETS_PORTAL_COPY]: canPortalCopy
    } = userPermissions;
    const {
      [FINDER_FILE_RENAME]: isRenaming
    } = actionsState;
    const isPageTemplate = templateType === TemplateTypes.landing_page.id;
    const isEmailTemplate = !!templateType && TemplateTypesById[templateType] && TemplateTypesById[templateType].categoryId === email.id;
    const noLayoutOrHaveDnDAccess = !isLayout || hasDnDTemplateAccess;
    const newPermissions = Object.assign({}, pick(userPermissions, Object.keys(FILE_PERMISSIONS)), {
      [FINDER_FILE_DUPLICATE]: canDuplicate && !isGlobalWidget && !isFileLocked && noLayoutOrHaveDnDAccess,
      [FINDER_FOLDER_MOVE_FILE]: canMove && !isRenaming && !isReadOnly && noLayoutOrHaveDnDAccess,
      [FINDER_FILE_SHOW_INFORMATION]: true,
      [FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE]: canCloneLayoutToTemplate && isLayout,
      [FINDER_FILE_CREATE_PAGE]: canCreatePage && (isLayout || isTemplate) && isPageTemplate && !isTemplatePartial && noLayoutOrHaveDnDAccess && isThemeAvailableForNewContent,
      [FINDER_FILE_CREATE_EMAIL]: canCreateEmail && (isLayout || isTemplate) && isEmailTemplate && !isTemplatePartial && noLayoutOrHaveDnDAccess,
      [FINDER_FILE_COPY_PUBLIC_URL]: canCopyPublicUrl && (isCodedFile || isRawAsset),
      [FINDER_FILE_COPY_MODULE_SNIPPET]: canCopyModuleSnippet && isCustomWidget,
      [FINDER_FILE_VIEW_LAYOUT_SOURCE]: canViewLayoutSource && (isLayout || isGlobalGroup),
      [FINDER_FILE_DELETE]: canDelete && !isReadOnly,
      [FINDER_FILE_RENAME]: canRename && !isReadOnly && noLayoutOrHaveDnDAccess,
      [FINDER_ASSETS_PORTAL_COPY]: canPortalCopy && !isMarketplaceAsset,
      [FINDER_BULK_PORTAL_COPY]: canPortalCopy && !isMarketplaceAsset
    });
    permissions = permissions.merge(newPermissions);
    return permissions;
  });
};
export const makeGetIsUpgradeableModule = () => {
  const getIsCustomWidget = makeGetIsCustomWidget();
  let isUpgradeableModule = false;
  return createSelector([getIsCustomWidget, getFileIdFromProps, getCustomWidgetsData], (isCustomWidget, fileId, customWidgets) => {
    const customWidget = customWidgets[fileId];

    if (isCustomWidget && customWidget && customWidget.schemaVersion === 1) {
      isUpgradeableModule = true;
    }

    return isUpgradeableModule;
  });
};
/**
 * Memoize file props
 */

export const makeGetFileProps = () => {
  const __getFilePermissions = makeGetFilePermissions();

  const __getFileActionsState = makeGetFileActionsState();

  const __getFileIsSelected = makeGetIsAssetSelected();

  const __getFileIsMultiSelected = makeGetIsAssetMultiSelected();

  let props = EMPTY_OBJECT;
  return createSelector([getFile, __getFilePermissions, __getFileActionsState, __getFileIsSelected, __getFileIsMultiSelected, getCurrentRenameAssetIsDuplicate], (file, filePermissions, fileActionsState, isSelected, isMultiSelected, isRenamingDuplicate) => {
    const {
      name: fileName,
      type: fileType,
      templateType: fileTemplateType,
      folderId,
      designAssetType: fileAssetType
    } = file || {};
    const {
      [FINDER_FILE_RENAME]: isRenamingFile
    } = fileActionsState;
    props = props.merge({
      file,
      fileName,
      filePermissions,
      fileTemplateType,
      fileType,
      fileAssetType,
      folderId,
      isRenamingDuplicate: isRenamingFile && isRenamingDuplicate,
      isRenamingFile,
      isSelected,
      isMultiSelected
    });
    return props;
  });
};
/**
 * Memoize if folder is user folder
 */

export const makeGetIsUserFolder = () => createSelector([getFolder], folder => folder && folder.generated === false);
/**
 * Memoize folder permissions
 */

const makeGetFolderActionsState = () => createSelector([getFolderIdFromProps, getCurrentRenameAssetId], (folderId, currentRenameAssetId) => {
  return {
    [FINDER_FOLDER_RENAME]: currentRenameAssetId === folderId
  };
});

export const makeGetFolderPermissions = () => {
  let permissions = DEFAULT_FOLDER_PERMISSIONS;

  const __getIsUserFolder = makeGetIsUserFolder();

  const __getFolderActionsState = makeGetFolderActionsState();

  return createSelector([getUserHasReadAccessOnly, getUserPermissions, getFolder, getIsInLockedFolderFactory, __getIsUserFolder, __getFolderActionsState, getIsThemeRootFolder, getActiveThemeFolderId, getIsServerlessFunctionFolder, getIsProjectsFolder, getIsAncestorServerlessFunctionFactory, getHasServerlessFunctions, getFolderIsOrIsInMarketplaceFolder, getIsServerlessDeploymentUngated, getHasMarketplaceListing], (userHasReadAccessOnly, userPermissions, folder, getIsInLockedFolder, isUserFolder, actionsState, isThemeRootFolder, activeThemeFolderId, isServerlessFunctionFolder, isProjectsFolder, isAncestorServerlessFunctionFolder, hasServerlessFunctions, isOrIsInMarketplaceFolder, isServerlessDeploymentUngated, hasMarketplacelisting) => {
    const {
      [FINDER_FOLDER_DELETE]: canDelete,
      [FINDER_FOLDER_DUPLICATE]: canDuplicate,
      [FINDER_FOLDER_RENAME]: canRename,
      [FINDER_FOLDER_MOVE_FOLDER]: canMove,
      [FINDER_ASSETS_PORTAL_COPY]: canPortalCopy
    } = userPermissions;
    const {
      [FINDER_FOLDER_RENAME]: isRenamingFolder
    } = actionsState;
    const folderId = folder && folder.id;
    const isLocked = getIsInLockedFolder(folderId);
    const ancestorIsServerlessFunctionFolder = isAncestorServerlessFunctionFolder(folderId);
    const isReadOnly = folder && folder.readOnly;
    const canWrite = !userHasReadAccessOnly && !isReadOnly && !isLocked && !isProjectsFolder;
    const canCreateNewFile = canWrite && (!isThemeRootFolder || !!activeThemeFolderId);
    const canCreateNewFolder = canWrite && (!isThemeRootFolder || !!activeThemeFolderId);
    const canCreateNewFunctions = canWrite && hasServerlessFunctions && !ancestorIsServerlessFunctionFolder;
    const canCreateNewTheme = canWrite && !isServerlessFunctionFolder && !isThemeRootFolder && !activeThemeFolderId && !ancestorIsServerlessFunctionFolder;
    const canCreateChildTheme = !userHasReadAccessOnly && !isServerlessFunctionFolder && isThemeRootFolder;
    const canDuplicateTheme = !userHasReadAccessOnly && !isServerlessFunctionFolder && !isOrIsInMarketplaceFolder && isThemeRootFolder;
    const canDeployFunctions = hasServerlessFunctions && isServerlessFunctionFolder && canCreateNewFunctions && isServerlessDeploymentUngated;
    const newPermissions = Object.assign({}, pick(userPermissions, Object.keys(FOLDER_PERMISSIONS)), {
      [FINDER_FILE_CREATE]: canCreateNewFile,
      [FINDER_FOLDER_CREATE]: canCreateNewFolder,
      [FINDER_FOLDER_DELETE]: canDelete && isUserFolder && !isLocked && !isReadOnly,
      [FINDER_FOLDER_DUPLICATE]: canDuplicate && isUserFolder && !isLocked && !isReadOnly && !isThemeRootFolder,
      [FINDER_FOLDER_RENAME]: canRename && isUserFolder && !isLocked && !isReadOnly && !hasMarketplacelisting,
      [FINDER_FOLDER_MOVE_FOLDER]: canMove && isUserFolder && !isRenamingFolder && !isLocked && !isReadOnly,
      [FINDER_THEMES_CREATE]: canCreateNewTheme,
      [FINDER_THEMES_DUPLICATE]: canDuplicateTheme && !isLocked,
      [FINDER_THEMES_CREATE_CHILD]: canCreateChildTheme && !isLocked,
      [FINDER_THEMES_CHILD_ADD_FILE]: !isLocked,
      [FINDER_ASSETS_PORTAL_COPY]: !isOrIsInMarketplaceFolder && canPortalCopy,
      [FINDER_BULK_PORTAL_COPY]: !isOrIsInMarketplaceFolder && canPortalCopy,
      [FINDER_FUNCTIONS_CREATE]: canCreateNewFunctions,
      [FINDER_FUNCTIONS_DEPLOY]: canDeployFunctions
    });
    permissions = permissions.merge(newPermissions);
    return permissions;
  });
};
/**
 * Memoize folder props
 */

export const makeGetFolderProps = () => {
  const __getFolderPermissions = makeGetFolderPermissions();

  const __getFolderActionsState = makeGetFolderActionsState();

  const __getIsUserFolder = makeGetIsUserFolder();

  const __getIsFolderSelected = makeGetIsAssetSelected();

  const __getIsFolderMultiSelected = makeGetIsAssetMultiSelected();

  let props = EMPTY_OBJECT;
  return createSelector([getFolder, __getFolderPermissions, __getFolderActionsState, __getIsUserFolder, __getIsFolderSelected, __getIsFolderMultiSelected, getActiveFolderId, getRevealedAssetId, getCurrentRenameAssetIsDuplicate], (folder, folderPermissions, folderActionsState, isUserFolder, isSelected, isMultiSelected, activeFolderId, revealedAssetId, isRenamingDuplicate) => {
    const {
      id: folderId,
      name: folderName,
      subcategory: folderSubcategory
    } = folder || {};
    const {
      [FINDER_FOLDER_RENAME]: isRenamingFolder
    } = folderActionsState;
    props = props.merge({
      folder,
      folderId,
      folderName,
      folderPermissions,
      folderSubcategory,
      isActive: revealedAssetId ? revealedAssetId === folderId : folderId === activeFolderId,
      isRenamingDuplicate: isRenamingFolder && isRenamingDuplicate,
      isRenamingFolder,
      isSelected,
      isMultiSelected,
      isUserFolder
    });
    return props;
  });
};
/**
 * Recently edited files
 */

const getRecentlyEditedFileIds = state => state.finder.files.recentlyEdited.fileIds;

export const getIsFetchingRecentlyEdited = state => state.finder.files.recentlyEdited.isFetching;
export const getRecentlyEditedFetchError = state => state.finder.files.recentlyEdited.fetchError;
export const getRecentlyEditedFiles = createSelector([getRecentlyEditedFileIds, getFilesById], (recentlyEditedIds, filesById) => {
  return recentlyEditedIds.map(__fileId => filesById[__fileId]);
});
/**
 * Recently deleted files
 */

const getRecentlyDeletedFileIds = state => state.finder.files.recentlyDeleted.fileIds;

export const getIsFetchingRecentlyDeleted = state => state.finder.files.recentlyDeleted.isFetching;
export const getRecentlyDeletedFetchError = state => state.finder.files.recentlyDeleted.fetchError;
export const getRecentlyDeletedFiles = createSelector([getRecentlyDeletedFileIds, getFilesById], (recentlyDeletedIds, filesById) => {
  return recentlyDeletedIds.map(__fileId => filesById[__fileId]);
});
/**
 * Search results
 */

export const getSearchResultsAssetIds = state => state.finder.assets.searchResults.assetIds;
export const getIsSearchingAssets = state => state.finder.assets.searchResults.isFetching;
export const getSearchAssetsOffset = state => state.finder.assets.searchResults.offset;
export const getSearchAssetsTotal = state => state.finder.assets.searchResults.total;
export const getSearchAssetsError = state => state.finder.assets.searchResults.fetchError;
export const getCurrentlyActiveSearchAssetId = state => state.finder.assets.searchResults.activeAsset.activeAssetId;
export const getSearchResultsAssets = createSelector([getSearchResultsAssetIds, getFilesById, getFoldersById], (searchResultsAssetIds, filesById, foldersById) => {
  return searchResultsAssetIds.map(__assetId => foldersById[__assetId] || filesById[__assetId]);
});
export const getActiveSearchResultAsset = createSelector([getCurrentlyActiveSearchAssetId, getFilesById, getFoldersById], (activeId, filesById, foldersById) => foldersById[activeId] || filesById[activeId]);
/**
 * Finder options
 */

export const getSavedOptions = createSelector([getShowRenameFileConfirmOption, getShowRenameFolderConfirmOption], (showRenameFileConfirm, showRenameFolderConfirm) => {
  return {
    showRenameFileConfirm,
    showRenameFolderConfirm
  };
});
/**
 * Bulk permissions
 */

const getBulkPermissions = state => {
  const selectedAssetsById = getSelectedAssetsById(state);
  const assetPermissions = Object.keys(selectedAssetsById || {}).filter(assetId => !!selectedAssetsById[assetId]).map(assetId => {
    const asset = selectedAssetsById[assetId];
    let canBulkCopy;
    let canDeleteAsset;
    let canMoveAsset;

    if (asset.assetType === FOLDER) {
      const getFolderPermissions = makeGetFolderPermissions();
      const folderPermissions = getFolderPermissions(state, {
        folderId: assetId
      });
      canBulkCopy = folderPermissions[FINDER_BULK_PORTAL_COPY];
      canDeleteAsset = folderPermissions[FINDER_FOLDER_DELETE];
      canMoveAsset = folderPermissions[FINDER_FOLDER_MOVE_FOLDER];
    } else {
      const getFilePermissions = makeGetFilePermissions();
      const filePermissions = getFilePermissions(state, {
        fileId: assetId
      });
      canBulkCopy = filePermissions[FINDER_BULK_PORTAL_COPY];
      canDeleteAsset = filePermissions[FINDER_FILE_DELETE];
      canMoveAsset = filePermissions[FINDER_FOLDER_MOVE_FILE];
    }

    return {
      [FINDER_BULK_PORTAL_COPY]: canBulkCopy,
      [FINDER_BULK_DELETE]: canDeleteAsset,
      [FINDER_BULK_MOVE]: canMoveAsset
    };
  });
  return Object.keys(BULK_PERMISSIONS).reduce((hash, permissionType) => {
    hash[permissionType] = assetPermissions.every(assetPermission => assetPermission[permissionType]);
    return hash;
  }, {});
};

export const makeGetBulkPermissions = () => {
  let permissions = DEFAULT_BULK_PERMISSIONS;
  return createSelector([getHasMultipleSelections, getBulkPermissions], (hasMultipleSelections, bulkPermissions) => {
    if (!hasMultipleSelections) {
      return DEFAULT_BULK_PERMISSIONS;
    }

    permissions = permissions.merge(bulkPermissions);
    return permissions;
  });
};
export const makeGetAssetBulkPermissions = () => {
  const _getIsAssetSelected = makeGetIsAssetSelected();

  let permissions = DEFAULT_BULK_PERMISSIONS;
  return createSelector([_getIsAssetSelected, getBulkPermissions], (isSelected, bulkPermissions) => {
    if (!isSelected) {
      return DEFAULT_BULK_PERMISSIONS;
    }

    permissions = permissions.merge(bulkPermissions);
    return permissions;
  });
};
export const getDuplicateFileFolderId = createSelector([getFile, getFoldersById, getRootFolderId], (file, foldersById, rootFolderId) => {
  let folderId;
  const folder = file && foldersById[file.folderId];

  if (folder && folder.subcategory === DESIGN_DEFAULT_LAYOUT) {
    folderId = rootFolderId;
  }

  return folderId;
});

const getWithExtension = (state, props) => {
  const {
    withExtension = true
  } = props;
  return withExtension;
};

const CODED_FILE_EXTENSION_REGEX = /\.(((min|hubl)\.)?(html|css|js))$/i;
export const getDuplicateFileName = createSelector([getFile, getFilesById, getDuplicateFileFolderId, getWithExtension], (file, filesById, duplicateFileFolderId, withExtension) => {
  const {
    name: __fileName,
    type: fileType
  } = file;
  let folderId = file.folderId;

  if (duplicateFileFolderId) {
    folderId = duplicateFileFolderId;
  }

  const regexMatch = __fileName.match(CODED_FILE_EXTENSION_REGEX);

  let fileExtension = '';

  if (regexMatch) {
    fileExtension = regexMatch[0];
  } else {
    fileExtension = `.${__fileName.split('.').pop()}`;
  }

  const fileNameWithoutExtension = __fileName.replace(fileExtension, '').trim();

  let duplicateName = generateCopyName(fileNameWithoutExtension, fileType, Object.keys(filesById).reduce((list, fileId) => {
    const _file = filesById[fileId];

    if (_file.type !== fileType) {
      return list;
    }

    if (folderId && _file.folderId !== folderId) {
      return list;
    }

    list.push(_file.merge({
      name: _file.name.replace(fileExtension, '').trim()
    }));
    return list;
  }, []));
  const assetsWithNoExtensionInDM = [LAYOUT, CUSTOM_WIDGET, GLOBAL_GROUP];

  if (fileExtension && withExtension && !assetsWithNoExtensionInDM.includes(fileType)) {
    duplicateName = `${duplicateName}${fileExtension}`;
  }

  return duplicateName;
});

const makeGetThemeConfigFileActions = () => {
  const getIsThemeConfigurationFile = makeGetIsThemeConfigurationFile();
  return createSelector([getIsThemeConfigurationFile, getIsReadOnly], (isThemeConfigurationFile, isReadOnly) => {
    if (!isThemeConfigurationFile) return null;
    const secondaryActions = [];
    secondaryActions.push(FINDER_FILE_SHOW_INFORMATION);

    if (!isReadOnly) {
      secondaryActions.unshift(FINDER_FILE_SHOW_REVISIONS);
    }

    return SeamlessImmutable({
      primaryActions: null,
      secondaryActions,
      deleteAction: null
    });
  });
};

export const getIsFolderInTheme = createSelector([getFolder, getFolderAncestors], (folder, folderAncestors) => {
  return __isThemeRootFolder(folder) || folderAncestors.some(ancestor => __isThemeRootFolder(ancestor));
});

const makeGetLockedFileActions = () => {
  const getIsCustomWidget = makeGetIsCustomWidget();
  const getIsLayout = makeGetIsLayout();
  const getIsTemplate = makeGetIsTemplate();
  const getIsTemplatePartial = makeGetIsTemplatePartial();
  const getTemplateType = makeGetTemplateType();
  const deleteAction = null;
  return createSelector([getFileIsInLockedFolder, getIsCustomWidget, getIsLayout, getIsTemplate, getIsTemplatePartial, getTemplateType, getActiveThemeFolderId, getIsFolderInTheme], (isFileInLockedFolder, isCustomWidget, isLayout, isTemplate, isTemplatePartial, templateType, activeThemeFolderId, isFolderInTheme) => {
    const isPageTemplate = templateType === TemplateTypes.landing_page.id;

    if (!isFileInLockedFolder) {
      return null;
    }

    const primaryActions = !activeThemeFolderId ? [FINDER_FILE_DUPLICATE] : null;
    const secondaryActions = SECONDARY_ACTIONS_ORDER.reduce((list, action) => {
      switch (action) {
        case FINDER_FILE_COPY_PATH:
        case FINDER_FILE_SHOW_DEPENDENTS:
        case FINDER_FILE_SHOW_REVISIONS:
          list.push(action);
          break;

        case FINDER_FILE_SHOW_INFORMATION:
          list.push(action);
          break;

        case FINDER_ASSETS_PORTAL_COPY:
          if (!isFolderInTheme) list.push(action);
          break;

        case FINDER_FILE_COPY_PUBLIC_URL:
          if (!isCustomWidget) list.push(action);
          break;

        case FINDER_FILE_COPY_MODULE_SNIPPET:
          if (isCustomWidget) list.push(action);
          break;

        case FINDER_FILE_CREATE_PAGE:
          if ((isLayout || isTemplate) && !isTemplatePartial && isPageTemplate) list.push(action);
          break;

        default:
          break;
      }

      return list;
    }, []);
    return SeamlessImmutable({
      primaryActions,
      secondaryActions,
      deleteAction
    });
  });
};

const makeGetReadOnlyFileActions = () => {
  const getIsCustomWidget = makeGetIsCustomWidget();
  const deleteAction = null;
  return createSelector([getIsReadOnly, getIsCustomWidget, getActiveThemeFolderId], (isReadOnly, isCustomWidget, activeThemeFolderId) => {
    if (!isReadOnly) {
      return null;
    }

    const primaryActions = !activeThemeFolderId ? [FINDER_FILE_DUPLICATE] : null;
    const secondaryActions = SECONDARY_ACTIONS_ORDER.reduce((list, action) => {
      switch (action) {
        case FINDER_FILE_COPY_PATH:
        case FINDER_FILE_SHOW_DEPENDENTS:
          list.push(action);
          break;

        case FINDER_FILE_SHOW_INFORMATION:
          list.push(action);
          break;

        case FINDER_THEMES_CHILD_ADD_FILE:
          list.push(action);
          break;

        case FINDER_FILE_COPY_MODULE_SNIPPET:
          if (isCustomWidget) list.push(action);
          break;

        default:
          break;
      }

      return list;
    }, []);
    return SeamlessImmutable({
      primaryActions,
      secondaryActions,
      deleteAction
    });
  });
};

export const makeGetFileActions = () => {
  const getIsCustomWidget = makeGetIsCustomWidget();
  const getIsGlobalGroup = makeGetIsGlobalGroup();
  const getIsLayout = makeGetIsLayout();
  const getIsRawAsset = makeGetIsRawAsset();
  const getFilePermissions = makeGetFilePermissions();
  const getThemeConfigFileActions = makeGetThemeConfigFileActions();
  const getReadOnlyFileActions = makeGetReadOnlyFileActions();
  const getLockedFileActions = makeGetLockedFileActions();
  let fileActions = DEFAULT_FILE_ACTIONS;
  let secondaryActionsByType = DEFAULT_SECONDARY_ACTIONS;
  return createSelector([getIsCustomWidget, getIsGlobalGroup, getIsLayout, getIsRawAsset, getFileIsInMarketplaceFolder, getFilePermissions, getThemeConfigFileActions, getReadOnlyFileActions, getLockedFileActions, getHasDndTemplateAccess], (isCustomWidget, isGlobalGroup, isLayout, isRawAsset, isMarketplaceAsset, filePermissions, themeConfigFileActions, readOnlyFileActions, lockedFileActions, hasDnDTemplateAccess) => {
    if (themeConfigFileActions) {
      return themeConfigFileActions;
    }

    if (lockedFileActions) {
      return lockedFileActions;
    }

    if (readOnlyFileActions) {
      return readOnlyFileActions;
    }

    const {
      [FINDER_FILE_CREATE_PAGE]: canCreatePage,
      [FINDER_FILE_CREATE_EMAIL]: canCreateEmail,
      [FINDER_FILE_COPY_PUBLIC_URL]: canCopyPublicUrl
    } = filePermissions;
    const noLayoutOrHaveDnDAccess = !isLayout || hasDnDTemplateAccess;
    const newSecondaryActionsByType = [isLayout && FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE, !isLayout && FINDER_FILE_COPY_PATH, !isMarketplaceAsset && FINDER_ASSETS_PORTAL_COPY, FINDER_FILE_SHOW_INFORMATION, canCopyPublicUrl && FINDER_FILE_COPY_PUBLIC_URL, isCustomWidget && FINDER_FILE_COPY_MODULE_SNIPPET, canCreateEmail && noLayoutOrHaveDnDAccess && FINDER_FILE_CREATE_EMAIL, canCreatePage && noLayoutOrHaveDnDAccess && FINDER_FILE_CREATE_PAGE, !isRawAsset && FINDER_FILE_SHOW_DEPENDENTS, FINDER_FILE_SHOW_REVISIONS, (isLayout || isGlobalGroup) && FINDER_FILE_VIEW_LAYOUT_SOURCE].reduce((hash, type) => {
      return type ? hash.set(type, true) : hash;
    }, EMPTY_OBJECT);

    if (secondaryActionsByType === newSecondaryActionsByType) {
      return fileActions;
    }

    secondaryActionsByType = secondaryActionsByType.replace(newSecondaryActionsByType);
    fileActions = fileActions.merge({
      secondaryActions: SECONDARY_ACTIONS_ORDER.filter(action => secondaryActionsByType[action])
    });
    return fileActions;
  });
};
export const makeGetAssetActions = () => {
  const getIsAssetMultiSelected = makeGetIsAssetMultiSelected();
  const getFileActions = makeGetFileActions();
  return createSelector([getFileIdFromProps, getFolderIdFromProps, getIsAssetMultiSelected, getFileActions, getHasMultipleSelections, getIsRootFolder, getIsThemeRootFolder, getIsProjectsFolder, getIsFolderReadOnly, getIsServerlessFunctionFolder, getFolderIsOrIsInMarketplaceFolder, getIsUngatedForCustomThemeChildCreation], (fileId, folderId, isMultiSelected, fileActions, hasMultipleSelections, isRootFolder, isThemeRootFolder, isProjectsFolder, isFolderReadOnly, isServerlessFunctionFolder, isOrIsInMarketplaceFolder, isUngatedForCustomThemeChildCreation) => {
    const folderPortalCopyPrimaryActions = [FINDER_FOLDER_COPY_PATH, FINDER_FOLDER_DUPLICATE, ...(isOrIsInMarketplaceFolder ? [FINDER_FOLDER_RENAME] : [FINDER_ASSETS_PORTAL_COPY, FINDER_FOLDER_RENAME])];
    const childThemeCreatePrimaryActions = [FINDER_FOLDER_COPY_PATH, FINDER_THEMES_DUPLICATE, FINDER_THEMES_CREATE_CHILD, FINDER_ASSETS_PORTAL_COPY, FINDER_FOLDER_RENAME];
    const hubspotChildThemeCreatePrimaryActions = [FINDER_FOLDER_COPY_PATH, FINDER_THEMES_DUPLICATE, FINDER_THEMES_CREATE_CHILD];
    const marketplaceChildThemeCreatePrimaryActions = [FINDER_FOLDER_COPY_PATH, FINDER_THEMES_CREATE_CHILD];
    const folderChildThemeAddFilePrimaryActions = [FINDER_FOLDER_COPY_PATH, FINDER_FOLDER_DUPLICATE, FINDER_THEMES_CHILD_ADD_FILE];

    if (fileId || folderId) {
      if (isMultiSelected) {
        return BULK_ACTIONS;
      } else if (fileId) {
        return fileActions;
      } else if (folderId && isOrIsInMarketplaceFolder && isThemeRootFolder) {
        return SeamlessImmutable.merge(MARKETPLACE_THEME_FOLDER_ACTIONS, {
          primaryActions: marketplaceChildThemeCreatePrimaryActions
        }, {
          deep: true
        });
      } else if (folderId && isFolderReadOnly && isThemeRootFolder) {
        return SeamlessImmutable.merge(HUBSPOT_THEME_FOLDER_ACTIONS, {
          primaryActions: hubspotChildThemeCreatePrimaryActions
        }, {
          deep: true
        });
      } else if (folderId && isThemeRootFolder) {
        return isUngatedForCustomThemeChildCreation ? SeamlessImmutable.merge(THEME_FOLDER_ACTIONS, {
          primaryActions: childThemeCreatePrimaryActions
        }, {
          deep: true
        }) : THEME_FOLDER_ACTIONS;
      } else if (folderId && isProjectsFolder) {
        return PROJECT_FOLDER_ACTIONS;
      } else if (folderId && isFolderReadOnly) {
        return SeamlessImmutable.merge(READONLY_FOLDER_ACTIONS, {
          primaryActions: folderChildThemeAddFilePrimaryActions
        }, {
          deep: true
        });
      } else if (folderId && isServerlessFunctionFolder) {
        return SeamlessImmutable.merge(SERVERLESS_FOLDER_ACTIONS, {
          primaryActions: folderPortalCopyPrimaryActions
        }, {
          deep: true
        });
      } else if (folderId && !isRootFolder) {
        return SeamlessImmutable.merge(FOLDER_ACTIONS, {
          primaryActions: folderPortalCopyPrimaryActions,
          secondaryActions: [FINDER_FOLDER_OPEN_EDITOR, FINDER_FOLDER_TOGGLE_LOCK]
        }, {
          deep: true
        });
      }
    }

    if (hasMultipleSelections) {
      return BULK_ACTIONS;
    }

    return DEFAULT_ACTIONS;
  });
};
export const makeGetAssetPermissions = () => {
  const _getBulkPermissions = makeGetBulkPermissions();

  const getFilePermissions = makeGetFilePermissions();
  const getFolderPermissions = makeGetFolderPermissions();
  const getIsAssetMultiSelected = makeGetIsAssetMultiSelected();
  return createSelector([getFileIdFromProps, getFolderIdFromProps, getIsAssetMultiSelected, _getBulkPermissions, getFilePermissions, getFolderPermissions, getHasMultipleSelections, getIsRootFolder, getIsThemeRootFolder], (fileId, folderId, isMultiSelected, bulkPermissions, filePermissions, folderPermissions, hasMultipleSelections, isRootFolder, isThemeRootFolder) => {
    if (fileId || folderId) {
      if (isMultiSelected) {
        return bulkPermissions;
      } else if (fileId) {
        return filePermissions;
      } else if (folderId && (!isRootFolder || isThemeRootFolder)) {
        return folderPermissions;
      }
    }

    if (hasMultipleSelections) {
      return bulkPermissions;
    }

    return DEFAULT_ASSET_PERMISSIONS;
  });
};
export const makeGetCanMoveSelectedAssets = () => createSelector([getUserHasReadAccessOnly, getFolder, getSelectedAssets, getActiveThemeFolderId, getIsLocked, getIsAncestorLockedFactory], (userHasReadAccessOnly, targetFolder, selectedAssets, activeThemeFolderId, isLocked, getIsAncestorLocked) => {
  if (!targetFolder) return false;
  const isAncestorLocked = getIsAncestorLocked(targetFolder.id);
  const props = {
    activeThemeFolderId,
    isLocked,
    isAncestorLocked
  };
  return selectedAssets.every(asset => {
    if (userHasReadAccessOnly) {
      return false;
    }

    return asset instanceof Folder ? canMoveFolder(targetFolder, asset, props) : canMoveFile(targetFolder, asset, props);
  });
});